import { useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { getAppDomain, SERVICE_NATION_BY_EVER_PRO_DOMAIN } from '../../config/domains';
import { Logger } from '../../config/logging/logger';
import { getServiceNationDestinationUrl } from '../utils/serviceNationLinks';
import { useServiceNationUrl } from '../utils/useServiceNationUrl';
import { NavigationTarget, useNavigationUtils } from '../utils/useNavigationUtils';

// Past this, the member is sent to the original link rather than waiting on a blank tab
export const SERVICE_NATION_TOKEN_TIMEOUT_MS = 10000;

const withTimeout = <T>(promise: Promise<T>, ms: number) => new Promise<T>((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error(`Timed out after ${ms}ms`)), ms);
  promise.then(resolve, reject).finally(() => clearTimeout(timer));
});

/**
 * Intercepts clicks on any link to a Service Nation page (hosted articles, Chameleon
 * experiences, etc.) and opens it through `/edge-auth` with a fresh SSO token, passing the link
 * as `destinationUrl` so the member lands on that exact page (path and query string) signed in.
 * A token is fetched per click because it only lives 5 minutes.
 *
 * The click is marked with `preventDefault()`. A React `onClick` on the same link runs
 * after this listener and must skip its own navigation when `event.defaultPrevented` is set.
 *
 * Modified clicks (Cmd/Ctrl/Shift/Alt) and middle clicks are left to the browser, so they
 * keep their native behavior (background tab, new window, download) but open without a token.
 */
export const useServiceNationLinkInterceptor = () => {
  const { buildServiceNationUrlFromLink } = useServiceNationUrl();
  const { openExternalLink, openExternalLinkAsync } = useNavigationUtils();
  const logger = useMemo(() => Logger.create('useServiceNationLinkInterceptor'), []);
  const isServiceNationApp = getAppDomain() === SERVICE_NATION_BY_EVER_PRO_DOMAIN;

  useEffect(() => {
    if (!isServiceNationApp) return undefined;

    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;

      const destinationUrl = getServiceNationDestinationUrl(anchor.href);
      if (!destinationUrl) return;

      event.preventDefault();
      const { href } = anchor;

      // Never rejects: when the token request fails or hangs, the member gets the original link
      const buildUrl = () => withTimeout(
        buildServiceNationUrlFromLink(destinationUrl),
        SERVICE_NATION_TOKEN_TIMEOUT_MS,
      ).catch((error) => {
        logger.error(Logger.format('Failed to generate Service Nation SSO token', error));
        Sentry.captureException(error, {
          tags: { feature: 'serviceNationLinkSso' },
          extra: { destinationUrl },
        });
        return href;
      });

      if (anchor.target !== '_blank') {
        // Same-tab links navigate in place once the URL is ready, which needs no popup
        buildUrl().then((url) => openExternalLink(url, NavigationTarget.Webview));
        return;
      }

      // Opens the tab during the click, so the popup blocker does not stop it later
      openExternalLinkAsync(buildUrl).catch((error) => {
        logger.error(Logger.format('Failed to open Service Nation link in a new tab', error));
        openExternalLink(href);
      });
    };

    // Capture phase so links rendered outside React (articles, Chameleon) are caught too
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [
    isServiceNationApp,
    buildServiceNationUrlFromLink,
    openExternalLink,
    openExternalLinkAsync,
    logger,
  ]);
};
